import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import { isEqual, get } from 'lodash';
import { CSP } from '../schemas/rql';
import { echidnaUriTemplate } from './shared/url_helpers';

import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import LightboxImage from '@reverbdotcom/commons/src/components/lightbox_image';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import SpecList from '@reverbdotcom/commons/src/components/spec_list';
import CmsPageCards, { CMS2_PAGE } from '@reverbdotcom/commons/src/cms/components/cms_page_cards';

import {
  JUMPLINK_BLURB,
  JUMPLINK_GALLERY,
  JUMPLINK_SPECS,
  JUMPLINK_OVERVIEW,
  JUMPLINK_VIDEO,
} from './csp2_jumplinks';

const IMAGE_LARGE = 'large_images';
const IMAGE_SUPERSIZE = 'supersize_images';

interface IProps extends IUserContext {
  csp: CSP;
  loading?: boolean;
  setJumplinks?: Function;
  buyingGuideSlug?: string;
}

export class CSP2Sidebar extends React.Component<IProps, null> {
  componentDidMount() {
    this.updateJumplinks();
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.csp, prevProps.csp)) {
      this.updateJumplinks();
    }
  }

  updateJumplinks() {
    if (!this.props.setJumplinks) { return; }

    this.props.setJumplinks({
      [JUMPLINK_VIDEO]: !!this.getVideoSlugs().length,
      [JUMPLINK_GALLERY]: this.hasGalleryImages(),
      [JUMPLINK_OVERVIEW]: !!this.getOverview(),
      [JUMPLINK_SPECS]: !!this.getSpecs().length,
    });
  }

  hasGalleryImages() {
    return [
      IMAGE_LARGE,
      IMAGE_SUPERSIZE,
    ].every(transform => !!this.getCspImages(transform).length);
  }

  getGalleryImages(transform) {
    return this.getCspImages(transform).map(img => img.source);
  }

  getOverview() {
    return get(this.props.csp, 'summary');
  }

  getVideoSlugs() {
    const slugs = get(this.props.csp, 'videos.videoLinks', []);
    return slugs.map(s => get(s, 'youtubeVideoId'));
  }

  getCspImages(transform) {
    // lodash's 'get' only falls back if the result === undefined
    return get(this.props.csp, transform) || [];
  }

  getSpecs() {
    return get(this.props, 'csp.specs.productSpecs', []);
  }

  renderOverview() {
    if (!this.getOverview()) { return; }

    return (
      <div
        className="csp2-sidebar__section"
        id={JUMPLINK_OVERVIEW}
      >
        <h3 className="csp2-sidebar__header">
          {I18n.t('discovery.comparisonShoppingPages.jumpLinks.overview')}
        </h3>
        <div
          className="csp2-sidebar__description"
        >
          <SanitizedRender
            htmlTag="div"
            html={this.getOverview()}
          />
          {!!this.props.csp.body &&
            <p className="size-90 mb-0">
              <a
                href={`#${JUMPLINK_BLURB}`}
              >
                {I18n.t('discovery.comparisonShoppingPages.readMore')}
              </a>
            </p>
          }
        </div>
      </div>
    );
  }

  renderSpecs() {
    if (!this.getSpecs().length) { return; }

    return (
      <section
        className="csp2-sidebar__section"
        id={JUMPLINK_SPECS}
      >
        <h3 className="csp2-sidebar__header">
          {I18n.t('discovery.comparisonShoppingPages.jumpLinks.specs')}
        </h3>
        <div
          className="csp2-sidebar__specs"
        >
          <SpecList specs={this.getSpecs()} collapseList />
        </div>
      </section>
    );
  }

  renderVideo() {
    const videos = this.getVideoSlugs();
    if (!videos.length) { return; }

    return (
      <div
        className="csp2-sidebar__section"
        id={JUMPLINK_VIDEO}
      >
        <h3 className="csp2-sidebar__header">
          {I18n.t('discovery.comparisonShoppingPages.jumpLinks.video')}
        </h3>
        <LightboxImage
          thumbURLs={videos}
          imageURLs={videos}
          fullsizeURLs={videos}
          withControls
          useAspectOverride
        />
      </div>
    );
  }

  renderGallery() {
    if (!this.hasGalleryImages()) { return null; }
    const largeImages = this.getGalleryImages(IMAGE_LARGE);
    const imageAlts = largeImages.map((_, index) => (
      I18n.t('discovery.comparisonShoppingPages.imageAlt', { title: this.props.csp.title, index: index + 1 })
    ));

    return (
      <div
        className="csp2-sidebar__section"
        id={JUMPLINK_GALLERY}
      >
        <h3 className="csp2-sidebar__header">
          {I18n.t('discovery.comparisonShoppingPages.jumpLinks.gallery')}
        </h3>
        <LightboxImage
          thumbURLs={largeImages}
          imageURLs={largeImages}
          imageAlts={imageAlts}
          fullsizeURLs={this.getGalleryImages(IMAGE_SUPERSIZE)}
          withControls
        />
      </div>
    );
  }

  renderBuyingGuide() {
    if (!this.props.buyingGuideSlug) { return null; }

    return (
      <div
        className="csp2-sidebar__section csp2-sidebar__section--buying-guides"
      >
        <CmsPageCards
          pageFields={[
            { pageSource: CMS2_PAGE, slug: this.props.buyingGuideSlug },
          ]}
          layoutSection="sidebar"
        />
      </div>
    );
  }

  renderAdminSection() {
    if (!this.props.user.isAdmin) { return null; }

    const echidnaUri = echidnaUriTemplate.expand({
      section: 'product-matching',
      query: {
        csp_slug: this.props.csp.slug,
        condition: '',
        show_matched: true,
        show_unmatched: false,
        hidden: true,
        showsold: true,
      },
    });

    return (
      <div className="mt-2 bg-offwhite bg--lighten bd-1 bd-primary padding-2 mb-1">
        <h3 className="item2__sidebar-module__header icon-r-lock">
          {I18n.t('discovery.adminButtons.adminActions')}
        </h3>
        <a
          href={echidnaUri}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-1 button button--small width-100"
        >
          {I18n.t('discovery.adminButtons.openInEchidna')}
        </a>
      </div>
    );
  }

  render() {
    if (!this.props.csp) { return null; }

    const classes = classNames(
      'csp2-sidebar',
      { 'csp2-sidebar--loading': this.props.loading },
    );

    return (
      <div
        className={classes}
      >
        {this.renderOverview()}
        {this.renderVideo()}
        {this.renderGallery()}
        {this.renderSpecs()}
        {this.renderBuyingGuide()}
        {this.renderAdminSection()}
      </div>
    );
  }
}

export default withUserContext<IProps>(CSP2Sidebar);
